import React from 'react';
import { useSelector } from 'react-redux';
import { Row, Col, Table, Tag, Button, Input, Select, Modal, Typography, Divider } from 'antd';
import locale from 'antd/es/locale/pt_BR';
import { ColumnsType } from 'antd/es/table';
import moment from 'moment';

import { stateGlobal } from '../../interfaces/Global';
import { IFeedbackIA } from './interface';

import { useGetColumnSearchPropsTypeString } from '../../hooks/FilterTable/objString';
import { useColumnDateTimeSearch } from '../../hooks/FilterTable/date';
import { useFeedbacksIA } from './useFeedbacksIA';
import MarkdownRenderer from './components/MarkdownRenderer';
import './styles.css';

const { Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

export const getCategoriaLabel = (categoria?: string): string => {
    switch (categoria) {
        case 'dado_incorreto':
            return 'Dado incorreto';
        case 'alucinacao':
            return 'Alucinação';
        case 'nao_entendeu':
            return 'Não entendeu';
        case 'formatacao':
            return 'Formatação';
        case 'outro':
            return 'Outro';
        default:
            return categoria || 'Geral';
    }
};

export const getStatusTag = (status?: string) => {
    const s = (status || '').toLowerCase();
    switch (s) {
        case 'resolvido':
            return <Tag color="green">RESOLVIDO</Tag>;
        case 'em_analise':
            return <Tag color="blue">EM ANÁLISE</Tag>;
        case 'descartado':
            return <Tag color="red">DESCARTADO</Tag>;
        case 'pendente':
        default:
            return <Tag color="orange">PENDENTE</Tag>;
    }
};

const InteligenciaArtificial: React.FC = () => {
    const propsState = useSelector((state: stateGlobal) => state.global);
    const isDark = propsState.theme === 'dark';

    const {
        clickBotaoVisualizar,
        hasSelectedView,
        loadingTable,
        onclickTable,
        rowSelection,
        data,
        refContagemDoFiltro,
        handleResetRowSelection,
        savingStatus,
        isModalOpen,
        selectedFeedback,
        formStatus,
        formObservacao,
        setFormStatus,
        setFormObservacao,
        closeModal,
        handleSaveStatus,
    } = useFeedbacksIA();

    const columns: ColumnsType<IFeedbackIA> = [
        {
            title: 'Usuário',
            key: 'usuario',
            width: 170,
            ellipsis: true,
            ...useGetColumnSearchPropsTypeString('usuario_nome'),
            render: (_, record) => {
                const nome = record.usuario_nome || record.usuario?.nome;
                if (nome) {
                    return <Text strong>{nome}</Text>;
                }
                if (record.usuario_id) {
                    return <Text>ID: {record.usuario_id}</Text>;
                }
                return <Text type="secondary">-</Text>;
            },
        },
        {
            title: 'Descrição',
            dataIndex: 'descricao',
            key: 'descricao',
            ellipsis: true,
            ...useGetColumnSearchPropsTypeString('descricao'),
            render: (descricao: string) => (
                <Text className="ellipsis-2-lines">{descricao || '-'}</Text>
            ),
        },
        {
            title: 'Categoria',
            dataIndex: 'categoria',
            key: 'categoria',
            width: 170,
            filters: [
                { text: 'Dado incorreto', value: 'dado_incorreto' },
                { text: 'Alucinação', value: 'alucinacao' },
                { text: 'Não entendeu', value: 'nao_entendeu' },
                { text: 'Formatação', value: 'formatacao' },
                { text: 'Outro', value: 'outro' },
            ],
            onFilter: (value, record) => record.categoria === value,
            render: (categoria: string) => {
                return <Tag style={{ borderRadius: 4 }}>{getCategoriaLabel(categoria)}</Tag>;
            },
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 130,
            filters: [
                { text: 'Pendente', value: 'pendente' },
                { text: 'Em análise', value: 'em_analise' },
                { text: 'Resolvido', value: 'resolvido' },
                { text: 'Descartado', value: 'descartado' },
            ],
            onFilter: (value, record) => (record.status || '').toLowerCase() === String(value).toLowerCase(),
            render: (status: string) => getStatusTag(status),
        },
        {
            title: 'Recebido em',
            dataIndex: 'criado_em',
            key: 'criado_em',
            width: 170,
            ...useColumnDateTimeSearch('criado_em'),
            render: (dataVal: string | Date, record) => {
                const valor = dataVal || record.created_at;
                return valor ? moment(valor).format('DD/MM/YYYY HH:mm') : '-';
            },
        },
    ];

    const perguntaTexto =
        selectedFeedback?.pergunta_usuario ||
        selectedFeedback?.mensagem_pergunta ||
        selectedFeedback?.pergunta;

    const respostaTexto =
        selectedFeedback?.resposta_ia ||
        selectedFeedback?.mensagem_resposta ||
        selectedFeedback?.conteudo;

    const nomeUsuario =
        selectedFeedback?.usuario_nome ||
        selectedFeedback?.usuario?.nome ||
        (selectedFeedback?.usuario_id ? `ID: ${selectedFeedback.usuario_id}` : '-');

    const municipioTexto =
        selectedFeedback?.municipio ||
        selectedFeedback?.cidade ||
        selectedFeedback?.usuario?.municipio ||
        selectedFeedback?.usuario?.cidade ||
        (selectedFeedback?.id_ibge ? `IBGE: ${selectedFeedback.id_ibge}` : '-');

    const dataCriacao =
        selectedFeedback?.criado_em || selectedFeedback?.created_at;

    return (
        <>
            <div
                style={{
                    position: 'fixed',
                    zIndex: 2,
                    paddingTop: 24,
                    paddingBottom: 10,
                    paddingLeft: 24,
                    paddingRight: 10,
                    margin: -24,
                }}
            >
                <Row>
                    <Col>
                        <Button
                            type="primary"
                            style={{ marginRight: 5, marginBottom: 5 }}
                            onClick={clickBotaoVisualizar}
                            disabled={!hasSelectedView}
                        >
                            Visualizar
                        </Button>
                    </Col>
                </Row>
            </div>

            <Table
                style={{
                    marginTop: 50,
                }}
                className="marginTopTables"
                rowClassName={(record, index) => 'ativoList'}
                rowKey={(record) => String(record._id || record.id || record.feedback_id)}
                loading={loadingTable}
                onRow={(record, rowIndex) => {
                    return {
                        onClick: () => { onclickTable(record, rowIndex) },
                    };
                }}
                rowSelection={rowSelection}
                columns={columns}
                dataSource={data}
                scroll={{ x: 1000 }}
                onChange={(pagination, filters, sorter, extra) => {
                    handleResetRowSelection();
                    refContagemDoFiltro.current = extra.currentDataSource;
                }}
                title={(registros) => {
                    return (
                        <div
                            style={{
                                backgroundColor: propsState.theme === 'light' ? '#FAFAFA' : '#1D1D1D',
                                padding: 10,
                                margin: -8,
                                marginBottom: 0.5,
                            }}
                        >
                            <Text>
                                {`Mostrando: ${registros.length} de ${refContagemDoFiltro.current.length === 0
                                    ? data.length
                                    : refContagemDoFiltro.current.length
                                    } num total de ${data.length} `}
                            </Text>
                        </div>
                    );
                }}
                footer={(registros) => {
                    return (
                        <div
                            style={{
                                textAlign: 'end',
                            }}
                        >
                            <Text>
                                {`Mostrando: ${registros.length} de ${refContagemDoFiltro.current.length === 0
                                    ? data.length
                                    : refContagemDoFiltro.current.length
                                    } num total de ${data.length} `}
                            </Text>
                        </div>
                    );
                }}
                size="small"
                pagination={{
                    locale: { items_per_page: '' },
                    showSizeChanger: true,
                    defaultPageSize: 100,
                    position: ['topRight', 'bottomRight'],
                    pageSizeOptions: ['10', '20', '50', '100'],
                }}
                locale={locale.Table}
            />

            {/* Modal de Detalhes e Resolução */}
            <Modal
                title="Detalhes do Feedback"
                open={isModalOpen}
                onCancel={closeModal}
                width={820}
                destroyOnClose
                footer={[
                    <Button key="close" onClick={closeModal} style={{ borderRadius: 6 }}>
                        Fechar
                    </Button>,
                    <Button
                        key="submit"
                        type="primary"
                        loading={savingStatus}
                        onClick={handleSaveStatus}
                        style={{ borderRadius: 6 }}
                    >
                        Salvar
                    </Button>,
                ]}
            >
                {selectedFeedback && (
                    <div>
                        {/* Header de Metadados Organizado */}
                        <div className={isDark ? 'feedback-modal-header-dark' : 'feedback-modal-header'}>
                            <Row gutter={[16, 12]} align="middle">
                                <Col xs={12} sm={6}>
                                    <span className="meta-label">Usuário</span>
                                    <div className="meta-value">{nomeUsuario}</div>
                                </Col>
                                <Col xs={12} sm={6}>
                                    <span className="meta-label">Município</span>
                                    <div className="meta-value">{municipioTexto}</div>
                                </Col>
                                <Col xs={12} sm={6}>
                                    <span className="meta-label">Recebido em</span>
                                    <div className="meta-value">
                                        {dataCriacao ? moment(dataCriacao).format('DD/MM/YYYY HH:mm') : '-'}
                                    </div>
                                </Col>
                                <Col xs={12} sm={6} style={{ textAlign: 'right' }}>
                                    <span className="meta-label">Categoria</span>
                                    <div style={{ marginTop: 2 }}>
                                        <Tag style={{ borderRadius: 4, marginRight: 0 }}>
                                            {getCategoriaLabel(selectedFeedback.categoria)}
                                        </Tag>
                                    </div>
                                </Col>
                            </Row>
                        </div>

                        {/* Descrição do Problema */}
                        <div style={{ marginBottom: 16 }}>
                            <div className="section-title">Descrição do problema</div>
                            <div className={isDark ? 'feedback-box-clean-dark' : 'feedback-box-clean'}>
                                {selectedFeedback.descricao || 'Nenhuma descrição fornecida.'}
                            </div>
                        </div>

                        {/* Contexto da Conversa com a IA */}
                        {(perguntaTexto || respostaTexto) && (
                            <div style={{ marginBottom: 16 }}>
                                {perguntaTexto && (
                                    <div style={{ marginBottom: 14 }}>
                                        <div className="section-title">Pergunta do usuário</div>
                                        <div className={isDark ? 'feedback-box-clean-dark' : 'feedback-box-clean'}>
                                            <div style={{ fontSize: 13.5, lineHeight: 1.5 }}>
                                                {perguntaTexto}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {respostaTexto && (
                                    <div>
                                        <div className="section-title">Resposta da IA</div>
                                        <div className={isDark ? 'feedback-box-clean-dark' : 'feedback-box-clean'}>
                                            <div className="feedback-chat-scroll">
                                                <MarkdownRenderer content={respostaTexto} isDark={isDark} />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        <Divider style={{ margin: '16px 0 14px 0' }} />

                        {/* Formulário de Resolução */}
                        <Row gutter={[16, 12]}>
                            <Col span={24}>
                                <div className="section-title">Status</div>
                                <Select
                                    style={{ width: '100%' }}
                                    value={formStatus}
                                    onChange={(val) => setFormStatus(val)}
                                >
                                    <Option value="pendente">Pendente</Option>
                                    <Option value="em_analise">Em análise</Option>
                                    <Option value="resolvido">Resolvido</Option>
                                    <Option value="descartado">Descartado</Option>
                                </Select>
                            </Col>

                            <Col span={24}>
                                <div className="section-title" style={{ marginTop: 10 }}>
                                    Observação interna
                                </div>
                                <TextArea
                                    rows={3}
                                    placeholder="Digite a observação interna sobre a correção ou análise..."
                                    value={formObservacao}
                                    onChange={(e) => setFormObservacao(e.target.value)}
                                />
                            </Col>
                        </Row>
                    </div>
                )}
            </Modal>
        </>
    );
};

export default InteligenciaArtificial;
